import { useEffect, useState, useCallback } from 'react';
import {
  useLoginWithEmail,
  useCreateWallet,
  useUser as usePrivyUser,
  getAccessToken,
} from '@privy-io/react-auth';
import { useToast } from '@/hooks/use-toast';
import { useTranslations } from 'next-intl';
import { useUser } from '../../context/userContext';
import { MANGIA_API_URL } from '../../lib/constants/mangia-api';
import { useSmartWallets } from '@privy-io/react-auth/smart-wallets';
import type { IUser } from '../../models/User';

interface AuthState {
  showCodeInput: boolean;
  otpValue: string;
  emailAddress: string;
  verificationError: string | null;
}

interface UserProfile {
  id: string;
  privy_user_id: string;
  email: string;
  role: string;
  wallet_address?: string;
  phone?: string;
  verified_credentials: string[];
  linked_accounts: string[];
  created_at: string;
  updated_at: string;
  last_privy_sync: string;
  privy_sync_status: string;
  tiktokUsername?: string;
}

// Type for the user context (plain object, not Mongoose document)
type AppUser = {
  _id?: string;
  email: string;
  address?: string;
  smartWalletAddress?: string;
  roles?: string[];
  profileImage?: string;
  name?: string;
  privyUserId?: string;
  tiktokUsername?: string;
  instagram?: {
    id?: string;
    username?: string;
    accountType?: string;
    mediaCount?: number;
    profilePictureUrl?: string;
    lastUpdated?: Date;
    followersCount?: number;
  };
  claimedCoupons?: {
    couponAddress: string;
    couponId: string | number;
    collaborationType: 'reel' | 'story';
    collaborationCashback: string;
    claimedAt?: Date;
  }[];
  lastLoginDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
};

interface UseAuthOTPProps {
  role?: string;
  onLoginSuccess?: (user: IUser) => void;
}

export const useAuthOTP = ({ role: _role, onLoginSuccess }: UseAuthOTPProps) => {
  const t = useTranslations('AuthWithOTPButton');
  const { client } = useSmartWallets();
  const { user, setUser } = useUser();

  const { toast } = useToast();
  const { refreshUser } = usePrivyUser();
  const [privyUser, setPrivyUser] = useState<unknown>(null);

  const [authState, setAuthState] = useState<AuthState>({
    showCodeInput: false,
    otpValue: '',
    emailAddress: '',
    verificationError: null,
  });

  const { createWallet } = useCreateWallet();

  // Function to fetch user profile from backend
  const fetchUserProfile = useCallback(async (token: string): Promise<UserProfile> => {
    const response = await fetch(`${MANGIA_API_URL}/api/v1/users/profile`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch user profile');
    }

    return response.json();
  }, []);

  // Function to handle post-login flow
  const handlePostLogin = useCallback(async () => {
    try {
      // Get JWT token from Privy
      const token = await getAccessToken();

      if (!token) {
        throw new Error('No access token available');
      }

      // Fetch user profile from backend
      const userProfile = await fetchUserProfile(token);

      // Convert UserProfile to AppUser format for context
      const appUser: AppUser = {
        _id: userProfile.id,
        email: userProfile.email,
        roles: [userProfile.role],
        privyUserId: userProfile.privy_user_id,
        smartWalletAddress: userProfile.wallet_address,
        tiktokUsername: userProfile.tiktokUsername,
        createdAt: new Date(userProfile.created_at),
        updatedAt: new Date(userProfile.updated_at),
      };

      // Update user context
      setUser(appUser as unknown as IUser);

      // Call success callback if provided
      if (onLoginSuccess) {
        onLoginSuccess(appUser as unknown as IUser);
      }
    } catch (error) {
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Authentication failed';
      toast({
        title: t('loginFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, [fetchUserProfile, setUser, onLoginSuccess, toast, t]);

  const {
    sendCode,
    loginWithCode,
    state: otpState,
  } = useLoginWithEmail({
    onComplete: async params => {
      try {
        // Refresh the identity token to ensure we have the latest one
        await refreshUser();

        // Create wallet for user (optional - can be done later)
        try {
          await createWallet();
        } catch (_walletError) {
          // continue without wallet
        }

        // Set Privy user
        if (params.user) {
          setPrivyUser(params.user);
        }

        // Handle post-login flow
        await handlePostLogin();
      } catch (error) {
        const errorMessage =
          error && typeof error === 'object' && 'message' in error
            ? String(error.message)
            : 'Authentication failed';
        setAuthState(prev => ({
          ...prev,
          verificationError: errorMessage,
        }));
        toast({
          title: t('loginFailed'),
          description: errorMessage,
          variant: 'destructive',
        });
      }
    },
    onError: error => {
      const errorMessage = String(error || 'Verification failed');
      setAuthState(prev => ({ ...prev, verificationError: errorMessage }));
      toast({
        title: t('loginFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (client && user && privyUser) {
      // Additional post-login logic if needed
    }
  }, [client, user, privyUser]);

  const handleSendCode = async (email: string) => {
    try {
      setAuthState(prev => ({
        ...prev,
        emailAddress: email,
        verificationError: null,
      }));

      await sendCode({
        email,
        disableSignup: false,
      });

      setAuthState(prev => ({
        ...prev,
        otpValue: '',
        showCodeInput: true,
      }));

      toast({
        title: t('codeSent'),
        description: t('codeSent'),
      });
    } catch (error) {
      const errorMessage = String(error || 'Failed to send code');
      setAuthState(prev => ({ ...prev, verificationError: errorMessage }));
      // Error toast is handled centrally in onError to avoid duplicates
    }
  };

  const handleVerifyCode = async (code: string) => {
    try {
      await loginWithCode({ code });
    } catch (error) {
      const errorMessage = String(error || 'Code verification failed');
      setAuthState(prev => ({ ...prev, verificationError: errorMessage }));
      // Error toast is handled centrally in onError to avoid duplicates
    }
  };

  const handleOTPChange = (value: string) => {
    setAuthState(prev => ({
      ...prev,
      otpValue: value,
      verificationError: null,
    }));
  };

  const handleOTPComplete = (value: string) => {
    setAuthState(prev => ({ ...prev, otpValue: value }));
    if (value.length === 6) {
      handleVerifyCode(value);
    }
  };

  const resetToEmailStep = () => {
    setAuthState({
      showCodeInput: false,
      otpValue: '',
      emailAddress: '',
      verificationError: null,
    });
  };

  const refreshIdentityToken = async () => {
    try {
      await refreshUser();
      toast({
        title: t('tokenRefreshed'),
        description: t('tokenRefreshed'),
      });
    } catch (error) {
      toast({
        title: t('tokenRefreshFailed'),
        description: String(error || 'Failed to refresh token'),
        variant: 'destructive',
      });
    }
  };

  const isSubmittingCode = otpState.status === 'submitting-code';
  const isSendingCode = otpState.status === 'sending-code';
  const isLoading =
    otpState.status === 'submitting-code' ||
    otpState.status === 'sending-code' ||
    otpState.status === 'done';

  return {
    authState,
    handleSendCode,
    handleVerifyCode,
    handleOTPChange,
    handleOTPComplete,
    resetToEmailStep,
    refreshIdentityToken,
    isSubmittingCode,
    isSendingCode,
    isLoading,
  };
};
